import { userPreferencesSchema, type ThemePreference } from '@pm-tool/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { meQuery } from '@/features/auth/auth-api';
import { featuresQuery } from '@/features/feature-flags/use-feature';
import { apiRequest } from '@/lib/api-client';
import {
  applyTheme,
  cachePreference,
  cachedPreference,
  effectivePreference,
  resolveTheme,
} from './theme';

/** Applies the effective color scheme to <html>; mounted once in the root route. */
export function useThemeSync() {
  const { data: me } = useQuery(meQuery);
  const { data: features } = useQuery(featuresQuery);
  const enabled = features?.flags.dark_mode;
  const account = me?.user.theme;
  const preference = effectivePreference({ enabled, account, cached: cachedPreference() });

  useEffect(() => {
    if (enabled === false) cachePreference(null);
    else if (account !== undefined) cachePreference(account);
  }, [enabled, account]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      applyTheme(resolveTheme(preference, media.matches));
    };
    apply();
    if (preference !== 'system') return;
    media.addEventListener('change', apply);
    return () => {
      media.removeEventListener('change', apply);
    };
  }, [preference]);
}

/** Changes the own preference; optimistic, rolled back when saving fails. */
export function useSetTheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (theme: ThemePreference) =>
      apiRequest({
        method: 'PUT',
        path: '/v1/me/preferences',
        body: { theme },
        schema: userPreferencesSchema,
      }),
    onMutate: async (theme) => {
      await queryClient.cancelQueries({ queryKey: meQuery.queryKey });
      const previous = queryClient.getQueryData(meQuery.queryKey);
      queryClient.setQueryData(meQuery.queryKey, (me) =>
        me ? { ...me, user: { ...me.user, theme } } : me,
      );
      return { previous };
    },
    onError: (_error, _theme, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(meQuery.queryKey, context.previous);
      }
    },
  });
}
